import React, {useCallback, useEffect, useState} from 'react';

import {addQueryToPath} from '../../../../../../libs/shared/helpers/src/browser/urlHelpers';
import {Handler} from '../../../../../../libs/shared/helpers/src/react/reactHelpers';
import {useUnmountCancelToken} from '../../../../../../srcCommon/helpers/hookHelpers';
import {FolderBreadcrumb} from '../../../../../components/folderBreadcrumbs/folderBreadcrumbs';
import {CancelToken} from '../../../../../helpers/asyncCancelHelpers';
import {useAppDispatch, useAppSelector} from '../../../../../helpers/reduxHelpers';
import {CompanyModel} from '../../../../../models/api/companyModel';
import {RuleBundleModel} from '../../../../../models/api/ruleBundleModel';
import {ApiTags} from '../../../../../models/api/tagModel';
import {fetchApiAsync} from '../../../../../services/http/http';
import {AppThunkDispatch} from '../../../../../store/appThunk';
import {getCompany} from '../../../../../store/data/company/companySelectors';
import {CustomFields} from '../../../../editors/commons/customFieldsModel';
import {RuleIntegration} from '../../../ruleIntegrationModel';
import {RuleEditorSkeleton} from '../../ruleEditorSkeleton';
import {createRecipeTag} from '../helpers/recipesHelpers';
import {CsatEditor} from './csatEditor';
import {CsatTagsModels} from './state/csatEditorState';

/*
 * Props.
 */

interface CsatEditorWrapperProps {
  namespace: string;
  ruleIntegration: RuleIntegration;
  customFields: CustomFields;
  breadcrumbs: ReadonlyArray<FolderBreadcrumb>;
  ruleBundle?: RuleBundleModel;
  isEditingRule: boolean;

  navigateToRules: Handler;
}

/*
 * Component.
 */

export const CsatEditorWrapper: React.FC<CsatEditorWrapperProps> = (props) => {
  const {namespace, breadcrumbs, isEditingRule} = props;

  const csatTags = useCsatTags(namespace);

  if (!csatTags) {
    return (
      <RuleEditorSkeleton
        breadcrumbs={breadcrumbs}
        namespace={namespace}
        isEditing={isEditingRule}
        isActive
      />
    );
  }

  // eslint-disable-next-line react/jsx-props-no-spreading
  return <CsatEditor {...props} csatTags={csatTags} />;
};

/*
 * Helpers.
 */

function useCsatTags(namespace: string) {
  const company = useAppSelector(getCompany);
  const cancelToken = useUnmountCancelToken();
  const [csatTags, setCsatTags] = useState<CsatTagsModels>();
  const dispatch = useAppDispatch();

  const fetchTags = useCallback(() => {
    fetchOrCreateCsatTags(dispatch, company, namespace, cancelToken).then(setCsatTags);
  }, [cancelToken, company, dispatch, namespace]);

  useEffect(() => {
    fetchTags();
  }, [fetchTags]);

  return csatTags;
}

async function fetchOrCreateCsatTags(
  dispatch: AppThunkDispatch,
  company: CompanyModel,
  namespace: string,
  cancelToken: CancelToken,
): Promise<CsatTagsModels> {
  const url = `${company.url}/search_tag2/survey`;

  const urlWithParams = addQueryToPath(url, {
    inbox_ids: null,
    namespaces: namespace,
    offset: 0,
    limit: 50,
    admin_override: true,
    behaviors: 'can_rule',
    tag_status: 'active',
  });

  const tags = await fetchApiAsync(ApiTags, urlWithParams, {cancelToken});

  // TODO: Migrate this to backend + do not use name to find CSAT tags.
  let sentTag = tags.find((tag) => tag.name.toLocaleLowerCase() === 'survey sent');
  let scheduledTag = tags.find((tag) => tag.name.toLocaleLowerCase() === 'survey scheduled');

  if (!sentTag) {
    sentTag = await createCsatSurveySent(namespace, dispatch);
  }

  if (!scheduledTag) {
    scheduledTag = await createCsatSurveyScheduled(namespace, dispatch);
  }

  return {
    scheduled: scheduledTag,
    sent: sentTag,
  };
}

async function createCsatSurveySent(namespace: string, dispatch: AppThunkDispatch) {
  return createRecipeTag('Survey sent', '📤', namespace, dispatch);
}

async function createCsatSurveyScheduled(namespace: string, dispatch: AppThunkDispatch) {
  return createRecipeTag('Survey scheduled', '⏰', namespace, dispatch);
}
